<template>
    <div class="autocomplete-filter-item">
        <e-field>
            <component
                :is="autocompleteComponent"
                v-bind="componentProps"
                class="autocomplete-component"
                @update:modelValue="handleUpdate"
                @input="handleInput"
            />
        </e-field>
    </div>
</template>

<script>
import ActivitySearch from '@/components/autocomplete/ActivitySearch.vue';
import CourseCodeSearch from '@/components/autocomplete/CourseCodeSearch.vue';
import CourseSearch from '@/components/autocomplete/CourseSearch.vue';
import GroupSearch from '@/components/autocomplete/GroupSearch.vue';
import TagSearch from '@/components/autocomplete/TagSearch.vue';
import UserSearch from '@/components/autocomplete/UserSearch.vue';

export default {
    name: 'AutocompleteFilterItem',

    emits: ['update:model-value'],

    components: {
        ActivitySearch,
        CourseCodeSearch,
        CourseSearch,
        GroupSearch,
        TagSearch,
        UserSearch,
    },

    props: {
        filter: {
            type: Object,
            required: true,
        },
        filterType: {
            type: String,
            required: true,
        },
        applyFilter: {
            type: Function,
            required: true,
        },
        applyFilterImmediate: {
            type: Function,
            required: true,
        },
        // Additional props that might be needed for specific autocomplete components
        course: {
            type: Object,
            default: null,
        },
        tagType: {
            type: String,
            default: '',
        },
        localManuallyAppliedGroup: {
            type: Object,
            default: null,
        },
    },

    computed: {
        autocompleteComponent() {
            const componentMap = {
                users: 'UserSearch',
                courses: 'CourseSearch',
                'course-codes': 'CourseCodeSearch',
                activities: 'ActivitySearch',
                groups: 'GroupSearch',
                tags: 'TagSearch',
            };

            return componentMap[this.filterType] || 'UserSearch';
        },

        componentProps() {
            const baseProps = {
                label: '',
            };

            // Add specific props based on filter type
            switch (this.filterType) {
                case 'activities':
                    return {
                        ...baseProps,
                        courseId: this.filter.courseId ?? null,
                    };

                case 'course-codes':
                    return {
                        ...baseProps,
                        filter: 'Course Codes',
                        course: this.course,
                        autofill: this.filter.autoApply,
                    };

                case 'groups':
                    return {
                        ...baseProps,
                        modelValue: this.localManuallyAppliedGroup,
                        setValue: this.localManuallyAppliedGroup?.name,
                    };

                case 'tags':
                    return {
                        ...baseProps,
                        type: this.tagType,
                    };

                case 'courses':
                    return {
                        ...baseProps,
                        modelValue: this.courseAutocompleteModelValue,
                    };

                default:
                    return baseProps;
            }
        },

        /** Controlled binding so clearing the filter (x) resets the input, not only the API state */
        courseAutocompleteModelValue() {
            const v = this.filter.value;
            const cleared = v == null || v === '' || v === false;

            if (cleared) {
                return null;
            }

            const title = this.filter.selectedTitle;

            return {
                id: v,
                title: typeof title === 'string' ? title : '',
            };
        },
    },

    mounted() {
        // Add class to parent containers to allow autocomplete overflow
        this.setupOverflowClasses();
    },

    beforeUnmount() {
        // Remove overflow classes
        this.removeOverflowClasses();
    },

    methods: {
        setupOverflowClasses() {
            const containers = [
                this.$el.closest('.dropdown'),
                this.$el.closest('.dropdown-menu'),
                this.$el.closest('.dropdown-content'),
                this.$el.closest('.filter-dropdown-item'),
                this.$el.closest('.filter-content'),
                this.$el.closest('.filter-section'),
            ];

            containers.forEach(container => {
                if (container) {
                    container.classList.add('has-autocomplete-overflow');
                }
            });
        },

        removeOverflowClasses() {
            const containers = [
                this.$el.closest('.dropdown'),
                this.$el.closest('.dropdown-menu'),
                this.$el.closest('.dropdown-content'),
                this.$el.closest('.filter-dropdown-item'),
                this.$el.closest('.filter-content'),
                this.$el.closest('.filter-section'),
            ];

            containers.forEach(container => {
                if (container) {
                    container.classList.remove('has-autocomplete-overflow');
                }
            });
        },
        handleUpdate(event) {
            let value = null;

            if (this.filterType === 'tags') {
                // Tags have a special structure
                value = event ? event.name.en : null;
            } else {
                // Most other autocomplete components return objects with id
                value = event ? event.id : null;
            }

            // Clear must not be debounced — otherwise stale modal state wins on the next refresh.
            if (value === null || value === undefined || value === '') {
                this.applyFilterImmediate(this.filter, value, event);

                return;
            }

            this.applyFilter(this.filter, value, event);
        },

        handleInput(event) {
            // For tag search which uses @input instead of @update:modelValue
            if (this.filterType === 'tags') {
                const value = event ? event.name.en : null;
                this.applyFilter(this.filter, value, event);
            }
        },
    },
};
</script>
